import { useEffect, useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag as alsBetrag, periodenname } from './api'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import {
  aktuelleSprache,
  buchungstext,
  beschriftung,
  bewegungsartname,
  gesellschaftsname,
  istVortrag,
  kontobezeichnung,
  kursangabe,
  kurswert,
  t,
  vortragsart,
  vortragsname,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { dimensionsart, ohneWert } from './Ohnewert'
import { belegmarken, belegverweise, herkuenfteZusammen, vorgangsmarken } from './Belegverweis'
import { liefersatz } from './Lieferstand'
import { saldowechselzusatz } from './Aufriss'
import { mitBis } from './Bisvermerk'
import { Namenszeile } from './Bericht'
import { betragskopf, mitLandeswaehrung } from './Anzeigewaehrung'
import type { Nachweiszeile, Wertenachweis as Nachweissatz } from './typen'
import { schichtname, spaltenname } from './Spaltennamen'
import { belegnamen, rechenwegtext, vereint } from './Rechenweg'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Der Wertenachweis — was hinter einer Zahl steht (2.14, R204–R209, R215).
 *
 * **Ein Fenster über dem Bericht, kein Ortswechsel.** Der Bericht bleibt
 * stehen, das Fenster geht wieder zu, und der Anwender ist da, wo er war. Ein
 * eigener Bildschirm nähme ihm den Zusammenhang, in dem die Frage entstanden
 * ist.
 *
 * Aufbau ist **Anfangsbestand plus Bewegungen je Periode** — dieselbe Form wie
 * ein Spiegel, und das ist kein Zufall: Nur so zeigen Nachweis und Spiegel
 * dieselbe Zahl (R186, R206). Erfolgskonten laufen von null, kumuliert.
 *
 * **Der Anwender wählt die Dimensionen** (R215). Der Dienst liefert die
 * vollständige Faktenzeile — Gesellschaft, Konto, Stufe, Bewegungsart,
 * Partner, Segment, die acht Analysedimensionen aus B9, lokale Währung, Kurs.
 * Angezeigt wird, was angehakt ist; alles andere wird **zusammengefasst**, und
 * die Summe bleibt dieselbe. LucaNet zeigt an dieser Stelle starr alles, was
 * das Datenmodell hergibt — das ist vollständig und unlesbar.
 */

/**
 * Wie eine Konsolidierungsspalte heißt.
 *
 * Ausgeschrieben statt aus der Kennung zusammengesetzt: Die Spalte
 * `zwerge_uv` heißt in der Oberfläche „ZwErgE UV", und ein zusammengebauter
 * Schlüssel liefe hier ins Leere, ohne dass der Übersetzer es merkte.
 */

export const ARTNAME: Record<string, Sprachschluessel> = {
  anfangsbestand: 'nachweis.art.anfangsbestand',
  bewegung: 'nachweis.art.bewegung',
  veraenderung: 'nachweis.art.veraenderung',
  endbestand: 'nachweis.art.endbestand',
  // Nur in einer anderen Anzeigewährung (2.12 Abschnitt 3.21, R995): Sie
  // lässt den Nachweis auf die umgerechnete Zelle aufgehen.
  waehrungsdifferenz: 'nachweis.art.waehrungsdifferenz',
}

/**
 * **Jede Zeile nennt ihre Herkunft mit Namen** (R824) — auch eine, die aus
 * keiner Buchung stammt: Sie trägt den Namen dessen, was sie rechnet. Bis
 * zum 2026-10-12 stand bei der Währungsdifferenz der Anzeigewährung „kein
 * einzelner Beleg“ — ein Platzhalter statt eines Namens.
 */
export const BELEGNAME_OHNE_BUCHUNG: Record<string, Sprachschluessel> = {
  waehrungsdifferenz: 'nachweis.beleg.stichtagskurs',
}

/** Welche Zelle gemeint ist — der vollständige Filter (R205). */
export type Nachweisziel = {
  kreis: string
  ledger: string
  art: 'bilanz' | 'guv'
  periode: number
  position: string
  positionsname: string
  konto: string | null
  einheit: string | null
  spalte: string
  /**
   * Der Bezug der **gelieferten** Sicht (R751, R771) — gesetzt heißt: ohne
   * Lauf, unmittelbar aus dem Journal. `kreis` steht dann leer, denn es gibt
   * keinen.
   */
  bezug?: string
  /** Was der Aufriss über Konto und Gesellschaft hinaus festgelegt hat (7.3 Abschnitt 2f). */
  weitere?: Record<string, string | null>
  /** Die Spiegelspalte, aus der eine Spiegelzelle stammt. */
  spiegelblock?: string
  spiegelspalte?: string
}

/**
 * Eine wählbare Spalte der Nachweistabelle.
 *
 * `schluessel` liefert den Wert, nach dem gruppiert wird — `null` heißt „nicht
 * belegt". `anzeige` macht daraus, was der Anwender liest. Beides getrennt,
 * weil zwei Zeilen mit derselben Kennung dieselbe Zeile sind, auch wenn die
 * Beschriftung sich einmal ändert.
 */
type Spaltenwahl = {
  id: string
  kopf: string
  /** Nach dieser Spalte wird gruppiert, solange sie angehakt ist. */
  schluessel: (z: Nachweiszeile) => string | null
  anzeige: (z: Nachweiszeile) => string | null
  zahl?: boolean
  /** Ausgegraut anbieten: Es gibt die Spalte, aber hier steht nichts drin. */
  leer?: boolean
}

const SPEICHER = 'coko.nachweis.spalten'

/**
 * Trennt die Felder im Gruppierungsschlüssel. Ein Steuerzeichen, damit kein
 * Kontoschlüssel und keine Kostenstelle es je enthält — sonst fielen zwei
 * verschiedene Zeilen zu einer zusammen.
 */
const TRENNER = String.fromCharCode(31)

/**
 * Was beim ersten Öffnen angehakt ist.
 *
 * Die Frage, die ein Konzernbuchhalter zuerst stellt: was hat sich bewegt, mit
 * wem, und wo steht es. Kostenstelle, Segment, Kurs und die lokale Währung
 * sind einen Haken entfernt — und wer sie einmal setzt, findet sie beim
 * nächsten Mal wieder (L2).
 */
const VORBELEGT = ['periode', 'was', 'partner', 'beleg', 'buchungstext']

/*
  **In der GuV ist die Bewegungsart nicht angehakt** (2.14 Abschnitt 6.10,
  R1100). Ein Erfolgskonto trägt immer Default (R1071); die Spalte sagte dort
  in jeder Zeile dasselbe.

  **Ihr Haken wird für die GuV getrennt gemerkt, alle anderen gemeinsam.** In
  der gemeinsamen Liste steht die Bewegungsart so, wie die Bilanz sie will —
  sonst nähme ein Haken in der GuV sie der Bilanz weg.
*/
const SPEICHER_GUV_BEWEGUNGSART = 'coko.nachweis.spalten.guv.bewegungsart'

function bewegungsartInDerGuv(): boolean {
  try {
    return localStorage.getItem(SPEICHER_GUV_BEWEGUNGSART) === 'an'
  } catch {
    /* siehe gemerkt() */
    return false
  }
}

/** Was beim Öffnen angehakt ist — gemerkt oder vorbelegt (R215, R1100). */
function anfangswahl(ziel: Nachweisziel): string[] {
  const wahl = gemerkt() ?? [...VORBELEGT, ...(ziel.konto === null ? ['einheit', 'konto'] : [])]
  if (ziel.art !== 'guv') return wahl
  return [...wahl.filter((x) => x !== 'was'), ...(bewegungsartInDerGuv() ? ['was'] : [])]
}

function gemerkt(): string[] | null {
  try {
    const roh = localStorage.getItem(SPEICHER)
    if (!roh) return null
    const liste: unknown = JSON.parse(roh)
    return Array.isArray(liste) ? liste.filter((x): x is string => typeof x === 'string') : null
  } catch {
    // Ein privates Fenster, gesperrte Site-Daten, ein kaputter Eintrag: Die
    // Vorbelegung tut es auch. Eine Spaltenwahl ist nichts, wofür man einen
    // Fehler anzeigt.
    return null
  }
}

function merke(ids: string[]): void {
  try {
    localStorage.setItem(SPEICHER, JSON.stringify(ids))
  } catch {
    /* siehe gemerkt() */
  }
}

export function Wertenachweis({
  ziel,
  aufSchliessen,
  aufBeleg,
}: {
  ziel: Nachweisziel
  aufSchliessen: () => void
  /**
   * In das Buchungsjournal springen — an die Belegzeile, nicht in eine Liste
   * (R209).
   *
   * **Zwei Wege, weil es zwei Herkünfte gibt:** `belegId` bei allem, was
   * geliefert wurde — seit Migration `0014` hat jede solche Zeile einen Beleg
   * —, `vorgangId` bei allem, was der Lauf selbst gebucht hat. Die
   * Konsolidierungsbuchungen bekommen ihren Beleg später (2.14, Abschnitt 3.2);
   * bis dahin klammert sie der Vorgang.
   */
  aufBeleg: (ziel: {
    lauf: number | null
    vorgangId: number | null
    belegId: number | null
    konto: string | null
    einheit: string | null
  }) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('wertenachweis:wertenachweis')

  const [satz, setSatz] = useState<Nachweissatz | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(false)
  /** Was dazu am Ort steht — nur ein fachlicher Satz (7.2 Abschnitt 30, R1003/R1004). */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)
  const [sichtbar, setSichtbar] = useState<string[]>(() => anfangswahl(ziel))

  // Die Belege dieses Nachweises, nach Kennung greifbar. Sie stehen einmal im
  // Kopf, nicht an jeder Zeile — ein Beleg kommt hier regelmäßig zwanzigmal vor
  // (R433).
  const marken = useMemo(() => belegmarken(satz?.belege), [satz])

  // Und die Rechenstufen, nach ihrem Code — damit auch eine gerechnete Zahl
  // in der Belegspalte ihren Namen trägt und nicht das Platzhalterwort (R824).
  const stufen = useMemo(() => vorgangsmarken(satz?.vorgaenge), [satz])
  const sprache = aktuelleSprache()

  useEffect(() => {
    let gilt = true
    setLaedt(true)
    setFehler(false)

    /*
      **Dasselbe Fenster über zwei Quellen** (R771): Der gerechnete Nachweis
      liest den Lauf, der gelieferte das Journal. Was danach kommt — Spalten,
      Gruppierung, die Probe unter der Tabelle — ist dasselbe, denn es ist
      dieselbe Auskunft über dieselbe Zahl.
    */
    const gemeinsam = {
      ledger: ziel.ledger,
      art: ziel.art,
      periode: ziel.periode,
      position: ziel.position,
      konto: ziel.konto,
      einheit: ziel.einheit,
      spalte: ziel.spalte,
      weitere: ziel.weitere,
      spiegelblock: ziel.spiegelblock,
      spiegelspalte: ziel.spiegelspalte,
    }

    ;(ziel.bezug
      ? api.gelieferterNachweis({ ...gemeinsam, bezug: ziel.bezug })
      : api.wertenachweis({ ...gemeinsam, kreis: ziel.kreis })
    )
      .then((s) => gilt && setSatz(s))
      .catch((f) => {
        if (gilt) {
          setFehler(true)
          setFehlersatz(fachlicherSatz(f))
        }
      })
      .finally(() => gilt && setLaedt(false))

    return () => {
      gilt = false
    }
  }, [ziel])

  // Escape schließt, und der Fokus wandert hinein — beides tut seit dem
  // 2026-10-06 das übliche Fenster selbst (7.2 Abschnitt 41, R1260). Eine
  // Funktion, die nur die Maus kennt, gibt es nicht (R143).

  // ---- Die wählbaren Spalten ------------------------------------------------
  //
  // Sie entstehen aus dem Nachweis und nicht aus einer festen Liste: Welche
  // Analysedimensionen es gibt und wie sie heißen, sagt der Kunde (B9). Eine
  // fest eingebaute „Kostenstelle" wäre genau der Fehler, den 2.4 vermeidet.
  const spalten = useMemo<Spaltenwahl[]>(() => {
    if (satz === null) return []

    const feste: Spaltenwahl[] = [
      {
        id: 'periode',
        kopf: t('nachweis.spalte.periode'),
        schluessel: (z) => (z.periode === null ? null : String(z.periode)),
        anzeige: (z) => (z.periode === null ? null : periodenname(z.periode)),
      },
      {
        id: 'einheit',
        kopf: t('nachweis.spalte.einheit'),
        schluessel: (z) => z.einheit,
        anzeige: (z) => (z.einheit === null ? null : gesellschaftsname(z.einheit)),
      },
      {
        id: 'konto',
        kopf: t('nachweis.spalte.konto'),
        schluessel: (z) => z.konto,
        // Ein Konto, das hier endet, sagt bis wann (7.3 Abschnitt 33, R1725).
        anzeige: (z) => (z.konto === null ? null : mitBis(kontobezeichnung(z.konto), z.giltBis)),
      },
      {
        id: 'stufe',
        kopf: t('nachweis.spalte.stufe'),
        /*
          **Gruppiert wird nach der Schicht, nicht nach der
          Konsolidierungsspalte** (R777, Auftrag vom 2026-09-26).

          Oben im Fenster steht die Gruppe, hier in der Zeile die einzelne
          Bewertungsebene — erst daran sieht der Anwender, was Rohdaten sind
          und was eine Anpassung.

          **Stünde hier weiter die Konsolidierungsspalte**, trügen alle
          gelieferten Zeilen denselben Wert; die Spalte gälte als durch die
          Zelle festgelegt und verschwände in den Kopf. Genau das tat sie bis
          zum 2026-09-26 — und mit ihr die Auskunft.
        */
        schluessel: (z) => z.schicht ?? z.spalte,
        anzeige: (z) =>
          schichtname(z.schicht, z.spalte, satz?.schichten, satz?.gruppe),
      },
      {
        id: 'was',
        kopf: t('nachweis.spalte.was'),
        schluessel: (z) => z.bewegungsart ?? z.art,
        anzeige: (z) =>
          // **Ein Vortrag heißt, was er ist** (R1065): ohne Vorjahresstand
          // steht die Eröffnung des ersten Monats als „Anfangsbestand" da,
          // nicht als „000 Startwert der Periode" (R1060).
          vortragsart(z.bewegungsart)
            ? vortragsname(vortragsart(z.bewegungsart)!)
            : z.bewegungsart
            ? bewegungsartname(z.bewegungsart)
            : ARTNAME[z.art]
              ? t(ARTNAME[z.art]!)
              : z.art,
      },
      {
        id: 'partner',
        kopf: t('nachweis.spalte.partner'),
        schluessel: (z) => z.icPartner,
        anzeige: (z) => (z.icPartner === null ? null : gesellschaftsname(z.icPartner)),
      },
      {
        id: 'segment',
        kopf: t('nachweis.spalte.segment'),
        schluessel: (z) => z.segment,
        anzeige: (z) => z.segment,
        leer: satz.zeilen.every((z) => z.segment === null),
      },
    ]

    // Die Analysedimensionen des Kunden, in ihrer Reihenfolge (B9).
    const analyse: Spaltenwahl[] = satz.dimensionen.map((d) => ({
      id: `dim${d.nummer}`,
      kopf: beschriftung(d.namen) || d.code,
      schluessel: (z) => z.dimensionen[d.nummer - 1] ?? null,
      anzeige: (z) => z.dimensionen[d.nummer - 1] ?? null,
      leer: !d.belegt,
    }))

    const werte: Spaltenwahl[] = [
      {
        /*
          **Der Buchungstext** (R395) — was der Buchende zu dieser Zahl
          geschrieben hat.

          Er steht **hinter** den Merkmalen und vor den Beträgen: Die Merkmale
          sagen, *welche* Zahl das ist, der Text sagt *warum sie da ist*, und
          die Zahl selbst steht rechts. Er ist die einzige Spalte des
          Nachweises, die ein Mensch geschrieben hat — und genau deshalb die,
          die eine Analyse am schnellsten weiterbringt.
        */
        id: 'buchungstext',
        kopf: t('nachweis.spalte.buchungstext'),
        // Verdichtet wird über den gespeicherten Text, gezeigt wird er mit Namen
        // (R838) und mit Monat und Jahr (7.2 Abschnitt 21, R839).
        schluessel: (z) => z.buchungstext,
        // Der Satz des Rechenkerns wird in der Sprache des Anwenders gesetzt (R1083).
        anzeige: (z) => buchungstext(z.buchungstext),
        leer: satz.zeilen.every((z) => !z.buchungstext),
      },
      {
        id: 'waehrung',
        kopf: t('nachweis.spalte.waehrung'),
        schluessel: (z) => z.waehrung,
        anzeige: (z) => z.waehrung,
      },
      {
        id: 'kurs',
        kopf: t('nachweis.spalte.kurs'),
        // **Eine Umrechnungszeile zeigt hier ihre beiden Kurse genau** (R1083):
        // Im Buchungstext stehen sie mit vier Stellen, der Prüfer findet den
        // genauen Wert in derselben Zeile.
        schluessel: (z) => (z.kurs === null ? kursangabe(z.buchungstext) : String(z.kurs)),
        // **Der Kurs steht wie in der Kurstabelle** (R43, R840): „1,0856" und
        // nicht „1.085600" — bis zum 2026-09-30 mit Punkt und festen sechs
        // Stellen, an der Schreibweise der Fußleiste vorbei.
        anzeige: (z) => (z.kurs === null ? kursangabe(z.buchungstext) : kurswert(z.kurs)),
        zahl: true,
        leer: satz.zeilen.every((z) => z.kurs === null && !kursangabe(z.buchungstext)),
      },
    ]

    return [...feste, ...analyse, ...werte]
  }, [satz])

  // ---- Was die Zelle festlegt, steht im Kopf --------------------------------
  //
  // **Ein Merkmal, das in jeder Zeile dasselbe sagt, ist keine Spalte.** Wer
  // den Nachweis einer Kostenstelle auf einem Konto einer Gesellschaft
  // öffnet, weiß Konto, Gesellschaft und Kostenstelle schon — drei Spalten,
  // die dreißigmal denselben Wert zeigen, sind kein Mehrwert, sondern Breite.
  // Sie wandern in den Kopf des Fensters und werden nicht mehr angeboten.
  //
  // Gleich, ob die Zelle sie festgelegt hat oder ob sie zufällig gleich
  // sind: Eine Position mit einem einzigen Konto zeigt ihr Konto ebenso im
  // Kopf. „Nicht zugeordnet" zählt nur, wenn der Filter es verlangt hat —
  // sonst hieße jede leere Dimension „festgelegt".
  const festgelegt = useMemo(() => {
    const ergebnis = new Map<string, string>()
    if (satz === null) return ergebnis

    const fakten = satz.zeilen.filter(
      (z) => z.art !== 'endbestand' && !(z.art === 'anfangsbestand' && z.konto === null),
    )
    if (fakten.length === 0) return ergebnis

    for (const s of spalten) {
      if (s.zahl || s.id === 'was' || s.id === 'periode') continue

      const erste = s.schluessel(fakten[0]!)
      if (!fakten.every((z) => s.schluessel(z) === erste)) continue

      const imFilter = s.id.startsWith('dim')
        ? s.id in (satz.filter ?? {})
        : s.id === 'partner'
          ? 'partner' in (satz.filter ?? {})
          : s.id === 'segment'
            ? 'segment' in (satz.filter ?? {})
            : false
      if (erste === null && !imFilter) continue

      const anzeige = s.anzeige(fakten[0]!)
      ergebnis.set(s.id, `${s.kopf}: ${anzeige ?? ohneWert(dimensionsart(s.id))}`)
    }

    return ergebnis
  }, [satz, spalten])

  /*
    **Eine Spalte, in der nirgends etwas steht, wird nicht gezeichnet.**

    Angeboten wird sie weiter — ausgegraut, damit der Anwender sieht, dass es
    sie gibt *und* dass hier nichts drinsteht. Aber sie als leere Spalte über
    dreißig Zeilen zu zeichnen, kostet Breite und sagt nichts; das ist genau
    das, was der Auftraggeber am 2026-09-12 angemahnt hat. Betrifft den
    Buchungstext an einer reinen Lieferung ebenso wie Segment oder Kurs.
  */
  /*
    **Die Bewertungsebene bleibt wählbar, auch wenn sie überall gleich ist**
    (R777, Auftrag vom 2026-09-26).

    *„Sonst kann ich nicht sehen, in welcher Zeile der entsprechende Delta-HGB-
    Beleg ist."*

    **Das ist eine Ausnahme von der Regel darüber, und sie ist begründet:** Wer
    eine Anpassung sucht, sucht genau diese Dimension. Steht heute nur eine
    Schicht da, ist das selbst die Auskunft — *alles Rohdaten* — und der
    Anwender will sie prüfen können, statt sie glauben zu müssen.

    **Der Kopf behält seine Angabe.** Beides zugleich: Solange alle Zeilen
    dieselbe Schicht tragen, steht sie oben; die Spalte ist trotzdem einen
    Haken entfernt. Vorbelegt ist sie nicht — sonst zeigte sie dreißigmal
    dasselbe Wort (7.3 Abschnitt 12).
  */
  const immerWaehlbar = (id: string) => id === 'stufe'

  const gewaehlt = useMemo(
    () =>
      spalten.filter(
        (s) => sichtbar.includes(s.id) && (immerWaehlbar(s.id) || !festgelegt.has(s.id)) && !s.leer,
      ),
    [spalten, sichtbar, festgelegt],
  )

  // ---- Zusammenfassen -------------------------------------------------------
  //
  // **Wegnehmen heißt zusammenfassen, nicht verstecken.** Wer die Kostenstelle
  // abwählt, bekommt eine Zeile statt dreien — und dieselbe Summe. Eine
  // ausgeblendete Spalte, unter der drei gleich aussehende Zeilen stehen
  // blieben, wäre die schlechtere Antwort.
  const zeilen = useMemo(() => {
    if (satz === null) return []

    const nachSchluessel = new Map<string, Nachweiszeile>()
    const folge: string[] = []

    for (const z of satz.zeilen) {
      // Die Randzeilen tragen keinen Aufriss und werden nie zusammengefasst.
      const schluessel =
        z.art === 'endbestand'
          ? 'endbestand'
          : [z.art, ...gewaehlt.map((s) => s.schluessel(z) ?? '')].join(TRENNER)

      const vorher = nachSchluessel.get(schluessel)
      if (vorher === undefined) {
        nachSchluessel.set(schluessel, { ...z })
        folge.push(schluessel)
        continue
      }

      // Zwei Zeilen werden eine. Was sie gemeinsam haben, bleibt stehen; was
      // sie unterscheidet, fällt weg — und ein Betrag in zwei Währungen ist
      // keine Summe, sondern eine Falle.
      const gleich = <T,>(a: T, b: T) => (a === b ? a : null)
      const waehrung = gleich(vorher.waehrung, z.waehrung)

      nachSchluessel.set(schluessel, {
        ...vorher,
        betrag: vorher.betrag + z.betrag,
        betragLokal:
          waehrung !== null && vorher.betragLokal !== null && z.betragLokal !== null
            ? vorher.betragLokal + z.betragLokal
            : null,
        waehrung,
        kurs: gleich(vorher.kurs, z.kurs),
        kurstyp: gleich(vorher.kurstyp, z.kurstyp),
        einheit: gleich(vorher.einheit, z.einheit),
        konto: gleich(vorher.konto, z.konto),
        spalte: gleich(vorher.spalte, z.spalte),
        bewegungsart: gleich(vorher.bewegungsart, z.bewegungsart),
        icPartner: gleich(vorher.icPartner, z.icPartner),
        segment: gleich(vorher.segment, z.segment),
        periode: gleich(vorher.periode, z.periode),
        dimensionen: vorher.dimensionen.map((w, i) => gleich(w, z.dimensionen[i] ?? null)),
        gerechnet: vorher.gerechnet || z.gerechnet,
        // Zwei Währungsdifferenzen werden eine: ihr Rechenweg die Summe beider (R1652).
        rechenweg: vereint(vorher.rechenweg, z.rechenweg),
        laufId: gleich(vorher.laufId, z.laufId),
        vorgangId: gleich(vorher.vorgangId, z.vorgangId),
        vorgangscode: gleich(vorher.vorgangscode, z.vorgangscode),
        // **Die Herkünfte fallen nicht weg, sie kommen zusammen** (R824).
        //
        // Bis zum 2026-09-27 stand hier nichts: Der Beleg der ERSTEN Zeile
        // blieb stehen und behauptete eine Herkunft, die für die zweite nicht
        // stimmte — während die Rechenseite ihn längst verwarf. Dieselbe
        // Regel an zwei Stellen war zwei Regeln, und die zweite war falsch.
        herkuenfte: herkuenfteZusammen(vorher.herkuenfte, z.herkuenfte),
      })
    }

    return folge.map((s) => nachSchluessel.get(s)!)
  }, [satz, gewaehlt])

  function umschalten(id: string) {
    setSichtbar((alt) => {
      const neu = alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]
      if (ziel.art !== 'guv') {
        merke(neu)
      } else if (id === 'was') {
        // Nur für die GuV gemerkt (R1100).
        try {
          localStorage.setItem(SPEICHER_GUV_BEWEGUNGSART, neu.includes('was') ? 'an' : 'aus')
        } catch {
          /* siehe gemerkt() */
        }
      } else {
        // Die Bewegungsart in der gemeinsamen Liste bleibt, wie die Bilanz sie hat.
        const inDerBilanz = (gemerkt() ?? VORBELEGT).includes('was')
        merke([...neu.filter((x) => x !== 'was'), ...(inDerBilanz ? ['was'] : [])])
      }
      return neu
    })
  }

  // Auf dem Ziel eines Kontowechsels trägt das Konto im Kopf denselben
  // Zusatz wie die Zeile im Bericht (2.7 Abschnitt 3.7b, R1623).
  const richtung =
    ziel.konto && satz?.saldowechsel?.istZiel && satz.saldowechsel.konten?.includes(ziel.konto)
      ? satz.saldowechsel.richtung
      : null
  // Und den Zusatz „· bis MM.JJJJ", wenn das Konto im letzten Monat des
  // Nachweises hier endet (7.3 Abschnitt 33, R1725).
  const kontoBis = ziel.konto
    ? satz?.zeilen.filter((z) => z.konto === ziel.konto && z.giltBis).at(-1)?.giltBis
    : undefined
  const konto = ziel.konto
    ? mitBis(
        richtung
          ? `${kontobezeichnung(ziel.konto)} ${saldowechselzusatz(richtung)}`
          : kontobezeichnung(ziel.konto),
        kontoBis,
      )
    : null
  const einheit = ziel.einheit === null ? null : gesellschaftsname(ziel.einheit)

  /*
    **Das übliche Fenster** (7.2 Abschnitt 41, R1260): verschieben am Kopf,
    groß und klein im Kopf, die Ecke zieht, Esc und Kreuz schließen. Es geht
    bildschirmfüllend auf wie jedes breite Arbeitsfenster (R834) — der
    Nachweis trägt bis zu vierzehn Spalten. Bis zum 2026-10-06 stand er
    eigens gebaut 1180 Punkte breit in der Mitte und ließ sich nicht bewegen.
  */
  return (
    <Dialogfenster
      titel={t('nachweis.titel')}
      weite="breit"
      aufSchliessen={aufSchliessen}
      kopfzusatz={
        /*
          Woher der Nachweis kommt, steht ausgeschrieben da: Position,
          Konto, Gesellschaft und **die Spalte**. Die Spalte ist der Teil,
          den man leicht vergisst und der den Unterschied macht — ohne sie
          beantwortet der Nachweis eine andere Frage als die gestellte
          (R205). Im Kopf des Fensters bleibt sie beim Rollen stehen.
        */
        [
          ziel.positionsname,
          konto,
          einheit,
          ziel.spiegelspalte
            ? `${t('nachweis.spiegelspalte')} ${t(`spiegel.spalte.${ziel.spiegelspalte}` as Sprachschluessel)}`
            : null,
          spaltenname(ziel.spalte, satz?.gruppe, satz?.schichten),
          periodenname(ziel.periode),
        ]
          .filter(Boolean)
          .join(' · ')
      }
    >
      <div className="nachweis">
        {/*
          Was in jeder Zeile gleich wäre, steht hier statt in den Spalten:
          die Kostenstelle, auf die aufgerissen wurde, das eine Konto der
          Position. Die Tabelle darunter zeigt nur noch, was sich
          unterscheidet.
        */}
        {/*
          **Eine verschobene Zahl sagt, dass sie verschoben ist** (R536).

          Der Satz steht über der Tabelle und nicht als Fußnote darunter:
          Er beantwortet die erste Frage, die ein Anwender hat, wenn unter
          einer Position Konten stehen, die ihr nicht zugeordnet sind —
          oder wenn unter einer Position gar nichts steht.
        */}
        {satz?.saldowechsel && (
          <p className="nachweisfilter saldowechsel">
            {/* Der Saldowechsel am Konto nennt die Konten (2.7 Abschnitt 3.7a, R1429). */}
            {satz.saldowechsel.konten && satz.saldowechsel.konten.length > 0
              ? t(satz.saldowechsel.istZiel ? 'nachweis.saldowechsel.konto.ziel' : 'nachweis.saldowechsel.konto.quelle', {
                  konten: satz.saldowechsel.konten.map((k) => kontobezeichnung(k)).join(' · '),
                  quelle: satz.saldowechsel.gebucht
                    .map((q) => beschriftung(q.namen) || q.code)
                    .join(' · '),
                  ziel:
                    beschriftung(satz.saldowechsel.ausgewiesen.namen) ||
                    satz.saldowechsel.ausgewiesen.code,
                })
              : satz.saldowechsel.istZiel
              ? t('nachweis.saldowechsel.ziel', {
                  quelle: satz.saldowechsel.gebucht
                    .map((q) => beschriftung(q.namen) || q.code)
                    .join(' · '),
                })
              : t('nachweis.saldowechsel.quelle', {
                  ziel:
                    beschriftung(satz.saldowechsel.ausgewiesen.namen) ||
                    satz.saldowechsel.ausgewiesen.code,
                })}
          </p>
        )}
        {festgelegt.size > 0 && (
          <p className="nachweisfilter festgelegt">
            {t('nachweis.festgelegt')}: {[...festgelegt.values()].join(' · ')}
          </p>
        )}
        {/*
          **Eine fortgeschriebene Zahl trägt ihre Herkunft** (2.12
          Abschnitt 3.20, R975): Der Startwert der Periode nennt als Beleg
          die Lieferung, aus der er stammt — der Satz sagt, aus welchem
          Monat.
        */}
        {satz?.lieferstand?.map((h) => (
          <p key={h.einheit} className="nachweisfilter lieferstand">
            {liefersatz(h, satz.bis)}
          </p>
        ))}

        {/*
          **Die Spaltenwahl** (R215). Sie steht im Kopf und nicht in einem
          Menü: Sie ist keine Einstellung, die man einmal trifft, sondern das
          Werkzeug, mit dem hier gearbeitet wird.
        */}
        {satz && spalten.length > 0 && (
          <fieldset className="dimensionswahl">
            <legend>{t('nachweis.wahl.titel')}</legend>
            <div className="haken">
              {spalten
                .filter((s) => immerWaehlbar(s.id) || !festgelegt.has(s.id))
                .map((s) => (
                <label key={s.id} className={s.leer ? 'gedaempft' : undefined}>
                  <input
                    type="checkbox"
                    checked={sichtbar.includes(s.id)}
                    onChange={() => umschalten(s.id)}
                  />
                  {s.kopf}
                  {s.leer && <span className="aufrissmarker">{t('nachweis.wahl.leer')}</span>}
                </label>
              ))}
              {satz && mitLandeswaehrung(satz) && (
                <label>
                  <input
                    type="checkbox"
                    checked={sichtbar.includes('betraglokal')}
                    onChange={() => umschalten('betraglokal')}
                  />
                  {t('nachweis.spalte.betraglokal')}
                </label>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={sichtbar.includes('beleg')}
                  onChange={() => umschalten('beleg')}
                />
                {t('nachweis.spalte.beleg')}
              </label>
            </div>
          </fieldset>
        )}

        {laedt && <p className="hinweis">{t('nachweis.laedt')}</p>}
        {fehlersatz && <p className="hinweis">{fehlersatz}</p>}
        {!laedt && !fehler && satz === null && <p className="hinweis">{t('nachweis.leer')}</p>}

        {satz && (
          <>
            <p className="erlaeuterung">
              {t(satz.spiegelspalte ? 'nachweis.aufbau.spiegel' : satz.istBestand ? 'nachweis.aufbau.bestand' : 'nachweis.aufbau.erfolg', {
                von: periodenname(satz.von),
                bis: periodenname(satz.bis),
              })}
            </p>

            <div className="tabellenrahmen">
              <table ref={spaltenrahmen} className="ziehbaretabelle">
                <Spaltenmassleiste masse={spaltenmasse} />
                <thead>
                  <tr>
                    {gewaehlt.map((s) => (
                      <th key={s.id} className={s.zahl ? 'zahl' : undefined}>
                        {s.kopf}
                      </th>
                    ))}
                    {sichtbar.includes('betraglokal') && mitLandeswaehrung(satz) && (
                      <th className="zahl">{t('nachweis.spalte.betraglokal')}</th>
                    )}
                    <th className="zahl">{betragskopf(satz.waehrung)}</th>
                    {sichtbar.includes('beleg') && <th>{t('nachweis.spalte.beleg')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((zeile, i) => {
                    const rand = zeile.art === 'anfangsbestand' || zeile.art === 'endbestand'

                    // **Der Endbestand ist eine Summe und keine Faktenzeile.**
                    // Er trägt keinen Aufriss, und „nicht zugeordnet" stünde
                    // dort wie eine Aussage über Kostenstellen, wo gar keine
                    // gemeint ist.
                    const summenzeile =
                      zeile.art === 'endbestand' ||
                      (zeile.art === 'anfangsbestand' && zeile.konto === null)

                    // Die Herkünfte dieser Zeile, fertig zum Anzeigen (R824).
                    // Meist eine, beim Zusammenfassen mehrere.
                    const verweise = belegverweise(
                      marken,
                      stufen,
                      sprache,
                      zeile.herkuenfte,
                      alsBetrag,
                    )

                    return (
                      <tr key={i} className={rand ? 'summe' : undefined}>
                        {gewaehlt.map((s) => {
                          const wert = s.anzeige(zeile)
                          return (
                            <td key={s.id} className={s.zahl ? 'zahl' : undefined}>
                              {/*
                                **„Nicht zugeordnet" ist die Sprache der
                                Dimensionen**, und nur ihre: Dort heißt es, dass
                                eine eingerichtete Dimension an dieser Zeile
                                keinen Wert trägt — eine echte Auskunft.

                                Beim **Buchungstext** wäre es falsch (R395):
                                Ein Satz wird nicht *zugeordnet*, er wird
                                geschrieben oder eben nicht. Hier steht deshalb
                                derselbe Gedankenstrich wie in den Summenzeilen.
                              */}
                              {/* Ein Konto, das hier endet: Gekürzt wird der Name, nicht der Zusatz (R1724). */}
                              {s.id === 'konto' && zeile.konto !== null && zeile.giltBis ? (
                                <Namenszeile bis={zeile.giltBis}>{kontobezeichnung(zeile.konto)}</Namenszeile>
                              ) : (
                                (wert ?? (
                                  <span className="gedaempft">
                                    {summenzeile || s.id === 'buchungstext'
                                      ? '—'
                                      : ohneWert(dimensionsart(s.id))}
                                  </span>
                                ))
                              )}
                              {/*
                                Der Anfangsbestand steht mit der Bewegungsart
                                da, unter der er geliefert wurde — und die ist
                                „Schlussbestand", weil er der des Vorjahres ist
                                (R206). Ohne das Wort daneben liest sich die
                                Zeile wie eine Bewegung des laufenden Jahres.
                              */}
                              {s.id === 'was' && zeile.art === 'anfangsbestand' && !summenzeile && !istVortrag(zeile.bewegungsart) && (
                                <span className="aufrissmarker">
                                  {t('nachweis.art.anfangsbestand')}
                                </span>
                              )}
                              {/*
                                Bis zum 2026-10-04 stand hier „gerechnet" (R207).
                                Wer gerechnet hat, sagt die Belegspalte mit dem
                                Namen der Rechenstufe (R824, R1041).
                              */}
                            </td>
                          )
                        })}

                        {sichtbar.includes('betraglokal') && mitLandeswaehrung(satz) && (
                          <td className={`zahl ${(zeile.betragLokal ?? 0) < 0 ? 'minus' : ''}`}>
                            {zeile.betragLokal === null ? (
                              <span className="gedaempft">—</span>
                            ) : (
                              <Betrag wert={zeile.betragLokal} />
                            )}
                          </td>
                        )}

                        {/* Der Rechenweg der Währungsdifferenz als Mouse-over (R1652). */}
                        <td
                          className={`zahl ${zeile.betrag < 0 ? 'minus' : ''}`}
                          title={zeile.rechenweg ? rechenwegtext(zeile.rechenweg) : undefined}
                        >
                          <Betrag wert={zeile.betrag} />
                        </td>

                        {sichtbar.includes('beleg') && (
                          <td>
                            {/*
                              **Der Endbestand hat keinen Beleg, weil er keiner
                              ist:** Er ist die Summe der Zeilen darüber. „ohne
                              Beleg" stünde dort wie ein Vorwurf an eine Zahl, die
                              gar keine Buchung sein will.

                              **Und der Anfangsbestand genauso**, wo er eine
                              Randzeile ist und keine Faktenzeile — bis zum
                              2026-09-27 stand über der Null des ersten
                              Monats „kein einzelner Beleg". Dieselbe Frage,
                              also dieselbe Antwort: derselbe Gedankenstrich.
                            */}
                            {summenzeile ? (
                              <span className="gedaempft">—</span>
                            ) : verweise.length > 0 ? (
                              /*
                                **Der Verweis STEHT IM NAMEN** (R433). Vorher
                                stand hier für jede
                                Zeile derselbe Satz „zum Beleg" — eine
                                Spalte, in der zwanzigmal dasselbe steht,
                                trägt nichts bei und kostet trotzdem Platz.

                                Jetzt trägt sie, was die Herkunft
                                unterscheidet: den Namen der Lieferung, sonst
                                ihre Nummer, und wo der Lauf selbst gebucht
                                hat, den Namen der Rechenstufe (R824).

                                **Und sie zählt alle auf.** Fallen zwei
                                Bewegungen in eine Zeile, stehen beide
                                Namen untereinander, jeder mit eigenem
                                Verweis — bis zum 2026-09-27 stand dort
                                „kein einzelner Beleg", also nichts.
                              */
                              <ul className="belegliste">
                                {verweise.map((v) => (
                                  <li key={v.schluessel}>
                                    <button
                                      type="button"
                                      className="ortsverweis belegverweis"
                                      title={v.titel}
                                      aria-label={v.beschriftung}
                                      onClick={() =>
                                        aufBeleg({
                                          lauf: v.lauf,
                                          vorgangId: v.vorgangId,
                                          belegId: v.belegId,
                                          konto: zeile.konto ?? ziel.konto,
                                          einheit: zeile.einheit ?? ziel.einheit,
                                        })
                                      }
                                    >
                                      {v.aufschrift}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              /*
                                **Ohne Beleg heißt hier wirklich ohne Beleg.**
                                Seit Migration 0014 trägt jede gelieferte Zeile
                                einen, und seit dem 2026-09-27 nennt auch die
                                zusammengefasste Zeile ihre Quellen. Was hier
                                steht, stammt aus keiner Buchung — und heißt
                                nach dem, was es rechnet (R824).
                              */
                              // Die Währungsdifferenz heißt nach dem Kurs, an den sie anpasst (R1653).
                              zeile.rechenweg ? (
                                <span>
                                  {belegnamen(zeile.rechenweg).map((name, i) => (
                                    <span key={name}>
                                      {i > 0 && <br />}
                                      {name}
                                    </span>
                                  ))}
                                </span>
                              ) : BELEGNAME_OHNE_BUCHUNG[zeile.art] ? (
                                <span>{t(BELEGNAME_OHNE_BUCHUNG[zeile.art]!)}</span>
                              ) : (
                                <span className="gedaempft">—</span>
                              )
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/*
              **Die Probe spricht nur, wenn sie etwas zu sagen hat** (R208,
              R1042). Geht der Nachweis auf, wiederholte der Satz nur den
              Endbestand der letzten Zeile. Geht er nicht auf, nennt er, was die
              Tabelle nicht zeigt: die Summe der Zeilen und die Abweichung.
            */}
            {satz.abweichung !== 0 && (
              <p className="probe weichtab">
                {t('nachweis.probe.weichtab', {
                  zelle: alsBetrag(satz.zelle),
                  summe: alsBetrag(satz.summe),
                  abweichung: alsBetrag(satz.abweichung),
                  waehrung: satz.waehrung,
                })}
              </p>
            )}
          </>
        )}
      </div>
    </Dialogfenster>
  )
}
